import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);
const API = '/api';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;       // 30 minutes
const IDLE_WARNING_MS = 25 * 60 * 1000;        // Warn at 25 minutes
const TOKEN_REFRESH_INTERVAL_MS = 20 * 60 * 1000; // Refresh token every 20 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('lg_token'));

  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const warningShownRef = useRef(false);

  const getAuthHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  // --- Idle Detection ---
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  }, []);

  const performLogout = useCallback(async (reason) => {
    try {
      const currentToken = localStorage.getItem('lg_token');
      if (currentToken) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      }
    } catch {}
    localStorage.removeItem('lg_token');
    setToken(null);
    setUser(null);
    if (reason === 'idle') {
      toast.info('Session expired due to inactivity. Please log in again.');
    }
  }, []);

  useEffect(() => {
    if (!token || !user) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    const handler = () => resetActivity();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));

    idleTimerRef.current = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        performLogout('idle');
      } else if (idle >= IDLE_WARNING_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        toast.warning('Your session will expire in 5 minutes due to inactivity.', { duration: 10000 });
      }
    }, 30000); // Check every 30 seconds

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, [token, user, resetActivity, performLogout]);

  // --- Silent Token Refresh ---
  useEffect(() => {
    if (!token || !user) return;

    const refreshToken = async () => {
      try {
        const currentToken = localStorage.getItem('lg_token');
        if (!currentToken) return;

        const res = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        const { token: newToken, user: updatedUser } = res.data;
        localStorage.setItem('lg_token', newToken);
        setToken(newToken);
        setUser(prev => ({ ...prev, ...updatedUser }));
      } catch (err) {
        if (err.response?.status === 401) {
          performLogout('expired');
        }
      }
    };

    refreshTimerRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [token, user, performLogout]);

  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      resetActivity();
    } catch {
      setUser(null);
      localStorage.removeItem('lg_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token, resetActivity]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('lg_token', t);
    setToken(t);
    setUser(u);
    resetActivity();
    return u;
  };

  const signup = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/signup`, { name, email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('lg_token', t);
    setToken(t);
    setUser(u);
    return u;
  };

  const googleLogin = async () => {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const res = await axios.get(`${API}/auth/google/url`, { params: { redirect_uri: redirectUri } });
    window.location.href = res.data.url;
  };

  const processGoogleCallback = async (code, redirectUri) => {
    const res = await axios.post(`${API}/auth/google/callback`, { code, redirect_uri: redirectUri });
    const { token: t, user: u } = res.data;
    localStorage.setItem('lg_token', t);
    setToken(t);
    setUser(u);
    resetActivity();
    return u;
  };

  const logout = async () => {
    await performLogout('manual');
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const apiCall = useCallback(async (method, path, data = null) => {
    resetActivity();
    // Always get fresh token from localStorage as fallback
    const currentToken = token || localStorage.getItem('lg_token');
    
    if (!currentToken && path !== '/auth/login' && path !== '/auth/signup') {
      console.warn('API call without token:', path);
    }
    
    const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
    // Send user's language preference so backend can translate content
    const lang = localStorage.getItem('lang') || localStorage.getItem('i18nextLng') || 'en';
    headers['Accept-Language'] = lang.split('-')[0].split('@')[0] || 'en';
    
    const config = {
      method,
      url: `${API}${path}`,
      headers,
    };
    if (data) config.data = data;
    try {
      const res = await axios(config);
      return res.data;
    } catch (err) {
      console.error('API error:', path, err.response?.status, err.response?.data);
      if (err.response?.status === 401) {
        performLogout('expired');
        return;
      }
      if (err.response?.status === 403 && err.response?.data?.subscription_required) {
        window.location.href = '/subscribe';
        return;
      }
      throw err;
    }
  }, [token, resetActivity, performLogout]);

  return (
    <AuthContext.Provider value={{
      user, loading, token, login, signup, googleLogin,
      processGoogleCallback, logout, updateUser, apiCall, checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
